/**
 * @namespace components
 */

import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Slider from 'react-slick';

import CustomPaging from '../../shared/CustomPaging/CustomPaging';
import { dataLayer, pauseAllVideos, useScreenSize } from '../../../helpers';
import { prepareSlideMarkup } from './helpers';

import './TextMediaCarousel.scss';
import AccessibleCarouselArrow from '../../shared/AccessibleCarouselButton/AccessibleCarouselButton';

/**
 * @class TextMediaCarousel
*
* Rich module for displaying video and image content in a rotating carousel.
*
* @memberof components
*
*
* @property {string} name - The configured name of the module
* @property {object} data - Primary object to insert data props into component
* @property {bool=} removeBottomPadding - Collapses bottom padding for the module
* @property {bool=} removeTopPadding - Collapses bottom padding for the module
* @property {array} data.slides - Array of slide data
* @property {string} data.slides.alignment - Controls the content alignemnt per slide. One of left, right, center, text-top-image-bottom, text-bottom-image-top
* @property {string=} data.slides.title - The title of the slide
* @property {string=} data.slides.subTitle - The subTitle of the slide
* @property {string=} data.slides.textColor - Any valid CSS color value as a string
* @property {string=} data.slides.titleTextColor - Any valid CSS color value as a string
* @property {string=} data.slides.subTitleTextColor - Any valid CSS color value as a string
* @property {string=} data.slides.backgroundColor - Any valid CSS color value as a string
* @property {string=} data.slides.backgroundImage - Any valid CSS color value as a string
* @property {array=} data.slides.media - Array of media data from Protein per slide
* @property {array=} data.slides.media.sources - Media sources per slide per media
* @example
* return (
    *   <TextMediaCarousel data={data} />
    * )
    */
   
   const TextMediaCarousel = (props = {}) => {
       const { isMobileScreen } = useScreenSize();
       const { name, data = {} } = props;
       
       const { logicalName, slides = [] } = data;
       
       const slideMarkup = prepareSlideMarkup(slides, props, isMobileScreen);
       
       /**
        * build the style props
       **/
      
      const containerClass = classNames({
          'container-fluid': true,
          module: true,
          'text-media-carousel': true,
    });

    /**
     * slider settings
     */

    const sliderSettings = {
        arrows: slides.length > 1,
        beforeChange: () => {
            pauseAllVideos();

            dataLayer.triggerEvent('slider_arrow_clicked', {
                id: logicalName,
                name,
            });
        },
        customPaging: CustomPaging,
        prevArrow: <AccessibleCarouselArrow />,
        nextArrow: <AccessibleCarouselArrow />,
        dots: slides.length > 1,
        speed: 500,
        infinite: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        adaptiveHeight: true,
        responsive: [
            {
                breakpoint: 992,
                settings: {
                    arrows: false,
                },
            },
        ],
    };

    return (
        <div className={containerClass} id={logicalName}>
            <div className="row">
                <div className="col-12">
                    <Slider {...sliderSettings}>{slideMarkup}</Slider>
                </div>
            </div>
        </div>
    );
};

TextMediaCarousel.propTypes = {
    name: PropTypes.string.isRequired,
    data: PropTypes.shape({
        logicalName: PropTypes.string.isRequired,
        slides: PropTypes.arrayOf(
            PropTypes.shape({
                alignment: PropTypes.oneOf([
                    'left',
                    'right',
                    'center',
                    'text-top-image-bottom',
                    'text-bottom-image-top',
                ]),
                title: PropTypes.string,
                subTitle: PropTypes.string,
                titleTextColor: PropTypes.string,
                subTitleTextColor: PropTypes.string,
                media: PropTypes.arrayOf(PropTypes.shape({ sources: PropTypes.array })),
                backgroundColor: PropTypes.string,
                backgroundImage: PropTypes.string,
                textColor: PropTypes.string,
            })
        ),
        removeBottomPadding: PropTypes.bool,
        removeTopPadding: PropTypes.bool,
    }),
};

export default TextMediaCarousel;
